import { useState, useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useDibsContracts } from './contracts/useDibsContracts';
import { useWeb3Connection } from './useWeb3Connection';
import { useTokenContractByAddress } from './useTokenContractByAddress';

import { useDibsSetApprovalForAll_viewer$key as viewer$key } from './__generated__/useDibsSetApprovalForAll_viewer.graphql';

/** @param {boolean} approved defaults to false */
type SetApprovalForAllCallback = (approved?: boolean) => Promise<void>;

/**
 * calls
 * contract.setApprovalForAll(dibs-contracts.auction/dibs-contracts/auctionV2,true)
 * on the specified contract
 */
export function useDibsSetApprovalForAll(
    contractAddress: string | undefined | null,
    viewerRef: viewer$key | null = null,
    isERC1155?: boolean
): [
    setApprovalForAll: SetApprovalForAllCallback,
    isApprovedForAll: boolean | null,
    inFlight: boolean
] {
    const contract = useTokenContractByAddress(contractAddress, isERC1155);
    const viewer = useFragment(
        graphql`
            fragment useDibsSetApprovalForAll_viewer on Viewer {
                isNftAuctionV2Enabled: featureFlag(feature: "nftAuctionV2")
                ...useMarketSettings_viewer
            }
        `,
        viewerRef
    );
    const isAuctionV2Enabled = viewer?.isNftAuctionV2Enabled;
    const { account, library } = useWeb3Connection();
    const [inFlight, setInFlight] = useState(false);
    const [contracts] = useDibsContracts();
    const [isApprovedForAll, setIsApprovedForAll] = useState<boolean | null>(null);
    const auctionAddress =
        contracts &&
        (contracts.auctionV2 && isAuctionV2Enabled ? contracts.auctionV2 : contracts.auction)
            .address;

    useEffect(() => {
        if (contract && account && auctionAddress) {
            contract.isApprovedForAll(account, auctionAddress).then(setIsApprovedForAll);
        }
    }, [account, auctionAddress, contract]);

    const setApprovalForAll: SetApprovalForAllCallback = useCallback(
        async (approved = false) => {
            if (contract && account && auctionAddress && library && !inFlight) {
                setInFlight(true);
                try {
                    const response = await contract
                        .connect(library.getSigner(account))
                        .setApprovalForAll(auctionAddress, approved);
                    await response.wait();
                } finally {
                    const update = await contract.isApprovedForAll(account, auctionAddress);
                    setIsApprovedForAll(update);
                    setInFlight(false);
                }
            }
        },
        [contract, account, auctionAddress, library, inFlight]
    );

    return [setApprovalForAll, isApprovedForAll, inFlight];
}
